import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deviceApi } from '../../services/admin/adminApi';
import { toast } from 'sonner';
import { queryKeys } from '../../lib/queryClient';
import type { DeviceUpdateRequest, Device, AssignPartnerRequest, AssignGroupRequest } from '../../types/admin';

/**
 * React Query hook for fetching paginated device list
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 100)
 * @param search - Search query string (optional)
 * @param sortBy - Field to sort by (optional)
 * @param sortDir - Sort direction (optional)
 * @returns Query result with device list and pagination info
 */
export function useDevices(
  page = 1,
  limit = 100,
  search = '',
  sortBy = '',
  sortDir: 'asc' | 'desc' = 'asc'
) {
  return useQuery({
    queryKey: ['admin', 'devices', page, limit, search, sortBy, sortDir],
    queryFn: async () => {
      console.log('🔍 useDevices: Fetching devices...', { page, limit, search, sortBy, sortDir });
      const result = await deviceApi.list(page, limit, search, sortBy, sortDir);
      console.log('✅ useDevices: API response:', result);

      // Throw error if API response indicates failure
      if (!result.success || !result.data) {
        throw new Error(result.error?.message || 'Failed to fetch devices');
      }

      return result;
    },
    select: (response) => {
      console.log('🔄 useDevices: select() received:', response);
      // At this point, we know response.data exists due to queryFn validation
      const selected = response.data!;
      console.log('🎯 useDevices: select() returning:', selected);
      return selected;
    },
  });
}

/**
 * Fetches the full device list across all backend pages, so the UI can
 * filter/sort/paginate client-side. Avoids the prod backend's broken
 * partial-serial / PAC search.
 */
export function useAllDevices() {
  return useQuery({
    queryKey: ['admin', 'devices', 'all'],
    queryFn: async () => {
      const all: Device[] = [];
      let page = 1;
      while (true) {
        const result = await deviceApi.list(page, 100, '', '', 'asc');
        if (!result.success || !result.data) {
          throw new Error(result.error?.message || 'Failed to fetch devices');
        }
        all.push(...result.data.devices);
        if (page >= result.data.pagination.pages) break;
        page += 1;
      }
      return all;
    },
    staleTime: 30_000,
  });
}

export function useInfiniteDevices(
  search = '',
  sortBy = '',
  sortDir: 'asc' | 'desc' = 'asc'
) {
  return useInfiniteQuery({
    queryKey: ['admin', 'devices', 'infinite', search, sortBy, sortDir],
    queryFn: async ({ pageParam = 1 }) => {
      const result = await deviceApi.list(pageParam, 50, search, sortBy, sortDir);

      if (!result.success || !result.data) {
        throw new Error(result.error?.message || 'Failed to fetch devices');
      }

      return result.data;
    },
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    initialPageParam: 1,
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
}

/**
 * React Query hook for fetching single device details
 * @param id - Device ID
 * @returns Query result with device details
 */
export function useDevice(id: number) {
  return useQuery({
    queryKey: ['admin', 'devices', id],
    queryFn: async () => {
      const result = await deviceApi.get(id);

      // Throw error if API response indicates failure
      if (!result.success || !result.data) {
        throw new Error(result.error?.message || 'Failed to fetch device');
      }

      return result;
    },
    select: (response) => response.data!,
    enabled: !!id,
  });
}

/**
 * React Query mutation hook for updating device
 * Invalidates device list and specific device queries on success
 * @returns Mutation result for device update
 */
export function useUpdateDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DeviceUpdateRequest }) =>
      deviceApi.update(id, data),
    onSuccess: (_, variables) => {
      // Invalidate device list and specific device
      queryClient.invalidateQueries({ queryKey: ['admin', 'devices'] });
      // A device update can move it between groups, which changes two things
      // this mutation does not own: the group's own roster (Manage Groups) and
      // the main-app device sidebar. Both showed the old group until somebody
      // reloaded the page — a false positive an operator has no reason to
      // doubt. useAssignGroup below already does the groups half; this is the
      // same reassignment reached from a different form.
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      toast.success('Device updated successfully');
    },
    onError: (error: any) => {
      console.error('Failed to update device:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to update device');
    },
  });
}

/**
 * Bulk-assign partner ownership to a list of devices.
 */
export function useAssignPartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignPartnerRequest) => deviceApi.assignPartner(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'devices'] });
      const updated = response?.data?.updated ?? 0;
      const partnerId = response?.data?.partnerUserId;
      toast.success(
        partnerId === null
          ? `Cleared partner from ${updated} device${updated === 1 ? '' : 's'}`
          : `Assigned partner to ${updated} device${updated === 1 ? '' : 's'}`,
      );
    },
    onError: (error: any) => {
      console.error('Failed to assign partner:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to assign partner');
    },
  });
}

/**
 * Bulk-assign devices to a group. locationId = 0 unassigns into the system
 * Unassigned group. Invalidates both device lists and the affected group's detail.
 */
export function useAssignGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignGroupRequest) => deviceApi.assignGroup(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'devices'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      const updated = response?.data?.updated ?? 0;
      const locationId = response?.data?.locationId;
      toast.success(
        locationId === 0
          ? `Unassigned ${updated} RIC${updated === 1 ? '' : 's'}`
          : `Moved ${updated} RIC${updated === 1 ? '' : 's'} to group`,
      );
    },
    onError: (error: any) => {
      console.error('Failed to assign group:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to assign group');
    },
  });
}
